import { ChangeEvent, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import useUpdateAdmin from './useUpdateAdmin';
import useGetAdminByIdState from './useGetAdminByIdState';
import { isPasswordStrong } from '../../../utils/Validators';
import { ROUTE_ADMIN } from '../../../components/Routing/routeHelpers';
import { AdminRole } from '../../AddAdminUser/hooks/useAddAdminUser';
import appLocalStorage from '../../../utils/AppLocalStorage';
import useAdminByEmailAPI from '../../AddAdminUser/hooks/useAdminByEmailAPI';

const useEditAdminUser = () => {
  const history = useHistory();
  const { state } = useLocation<{ tenant: string }>();
  const { adminId } = useParams<{ adminId: string }>();
  const { isLoading, oldAdminDetails, adminDetails, setAdminDetails } =
    useGetAdminByIdState(adminId, state?.tenant);
  const [updateAdmin] = useUpdateAdmin(adminDetails);
  const [isEmailChanged, setIsEmailChanged] = useState(false);

  const onEdit = () => {
    updateAdmin(adminId, adminDetails);
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAdminDetails({
      ...adminDetails,
      [e.target.id]: e.target.value,
    });
  };

  const areAdminDetailsUpdated = () => {
    if (
      adminDetails.email === oldAdminDetails.email &&
      adminDetails.firstName === oldAdminDetails.firstName &&
      adminDetails.lastName === oldAdminDetails.lastName &&
      adminDetails.tenant === oldAdminDetails.tenant
    ) {
      if (oldAdminDetails.organisations) {
        return adminDetails.organisations !== oldAdminDetails.organisations;
      }
      if (oldAdminDetails.memberships) {
        const adminOrg = adminDetails.organisations.map((d) => d.code);
        const oldAdminOrg = oldAdminDetails.memberships.map(
          (d: any) => d.organizationGlobal,
        );

        if (adminOrg.length !== oldAdminOrg.length) return true;
        return (
          new Set(adminOrg).size !== new Set([...adminOrg, ...oldAdminOrg]).size
        );
      }
      return false;
    }
    return true;
  };

  const getIsFormInValid = () => {
    const {
      firstName,
      lastName,
      email,
      confirmPassword,
      password,
      role,
      tenant,
      organisations,
    } = adminDetails;

    const areNamesEmpty = email === '' || firstName === '' || lastName === '';

    if (
      (role === AdminRole.ORGANIZATION_ADMIN ||
        role === AdminRole.ALEF_ORG_SUPPORT) &&
      (tenant === '' || organisations.length === 0)
    ) {
      return true;
    }

    if (password.length > 0 || confirmPassword.length > 0) {
      const isPassMismatch = password !== confirmPassword;
      return isPassMismatch || !isPasswordStrong(password);
    }

    return areNamesEmpty || !areAdminDetailsUpdated();
  };

  const onCancel = () => history.push(ROUTE_ADMIN.adminsPage);

  const { adminByEmail, message } = useAdminByEmailAPI({
    onComplete: (userId, isEmailExist) => {
      setIsEmailChanged(false);
    },
  });

  const onCheckEmail = () => {
    adminByEmail(adminDetails.email);
  };

  return {
    isLoading,
    isViewOnlyRole: appLocalStorage.isViewOnlyRole(),
    adminDetails,
    isFormInValid: getIsFormInValid(),
    onEdit,
    onInputChange,
    onCancel,
    isEmailChanged,
    setIsEmailChanged,
    messageText: message,
    onCheckEmail,
  };
};

export default useEditAdminUser;
